"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState } from "react";
import { Wine, MapPin, Calendar } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CartaDeVinos() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const wines = [{
    name: "Viñatigo Ensamblaje Blanco",
    origin: "Islas Canarias, Tenerife",
    year: "2018",
    description: "Vino blanco con aromas minerales/ahumados y fruta tropical.",
    image: "https://bodegajulian.es/wp-content/uploads/2026/02/Captura-de-pantalla-2026-02-07-a-las-18.23.48.png?v=ojo2bvgd2u"
  }, {
    name: "Carmelo Rodero 9 meses",
    origin: "Ribera del Duero",
    year: "2024",
    description: "Tempranillo. Frutos rojos y negros, boca fresca y equilibrada. 9 meses en barrica",
    image: "https://bodegajulian.es/wp-content/uploads/2026/02/Captura-de-pantalla-2026-02-07-a-las-18.31.23.png?v=8ulkqjtrfcv"
  }, {
    name: "Ramon Bilbao \"Selección\"",
    origin: "La Rioja",
    year: "2021",
    description: "Tempranillo. Fruta negra (mora, ciruela) con notas de vainilla/coco y regaliz. Jugoso y equilibrado.",
    image: "https://bodegajulian.es/wp-content/uploads/2026/02/Captura-de-pantalla-2026-02-07-a-las-18.38.57.png?v=b5c8wzmokw4"
  }, {
    name: "Canta Perdices",
    origin: "D.O. Ribera del Duero",
    year: "2023",
    description: "Tempranillo. Fruta madura con fondo especiado, estilo con paso por hormigon y barrica",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1539391/img/image_0ukuasp6.png"
  }, {
    name: "Tajinaste Tinto Roble",
    origin: "D.O.P. Islas Canarias",
    year: "2025",
    description: "Listan Negro y Tintilla. Frutos rojos/negros con notas de vainilla y tostados; buena estructura; 5 meses en barrica",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1539391/img/image_nul85wqx.png"
  }, {
    name: "Paisaje de Las Islas",
    origin: "D.O.P. Islas Canarias",
    year: "2021",
    description: "Malvasia aromática y marmajuelo. Blanco complejo con aromas florales y fruta",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1539391/img/image_nvlou8c0.png"
  }];
  return <section id="carta-de-vinos" ref={ref} className="dark bg-background text-foreground relative py-24 px-4 md:px-8 lg:px-16 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        <div className="absolute top-10 left-10 w-32 h-32 bg-primary/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-secondary/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-accent/10 rounded-full blur-2xl"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {}
        <div className={`text-center mb-16 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="inline-flex items-center gap-2 mb-4 text-primary-light">
            <Wine className="w-6 h-6" />
            <span className="text-sm uppercase tracking-wider font-semibold">
              Selección Exclusiva
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-display font-bold mb-4 text-foreground">
            Carta de Vinos
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">Descubre parte de nuestra colección de vinos con historia, cada botella cuenta una tradición única.</p>
        </div>

        {}
        <div className={`transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-6">
              {wines.map((wine, i) => <CarouselItem key={i} data-index={i} className="pl-4 md:pl-6 basis-full sm:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                  <HoverCard openDelay={100} closeDelay={100}>
                    <HoverCardTrigger asChild>
                      <div className="group relative cursor-pointer" onMouseEnter={() => setActiveIndex(i)} onMouseLeave={() => setActiveIndex(null)}>
                        <div className={`card h-[420px] flex flex-col items-center justify-center p-8 transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] ${activeIndex === i ? "scale-105 shadow-strong" : "scale-100"}`} style={{
                      transform: activeIndex === i ? "perspective(1000px) rotateY(5deg)" : "perspective(1000px) rotateY(0deg)"
                    }}>
                          {}
                          <div className="relative w-full h-64 mb-6 flex items-center justify-center">
                            <img src={wine.image} alt={wine.name} className="h-full w-auto object-contain transition-transform duration-[var(--duration-medium)] ease-[var(--ease-smooth)] group-hover:scale-110" />
                            {}
                            <div className={`absolute inset-0 bg-gradient-radial from-primary/10 to-transparent rounded-full blur-2xl transition-opacity duration-[var(--duration-medium)] ${activeIndex === i ? "opacity-100" : "opacity-0"}`}></div>
                          </div>

                          {}
                          <h3 className="text-xl font-display font-bold text-foreground text-center mb-2">
                            {wine.name}
                          </h3>
                          <p className="text-sm text-muted-foreground text-center">
                            {wine.year}
                          </p>
                        </div>
                      </div>
                    </HoverCardTrigger>

                    {}
                    <HoverCardContent side="top" align="center" className="w-72 glass-effect p-6 border-white/10">
                      <div className="space-y-3">
                        <h4 className="text-lg font-display font-bold text-foreground">
                          {wine.name}
                        </h4>
                        <div className="flex items-start gap-2 text-sm text-muted-foreground">
                          <MapPin className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                          <span>{wine.origin}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <SafeHydrate><Calendar className="w-4 h-4 text-primary flex-shrink-0" /></SafeHydrate>
                          <span>Añada {wine.year}</span>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed pt-2 border-t border-white/10">
                          {wine.description}
                        </p>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </CarouselItem>)}
            </CarouselContent>

            {}
            <div className="flex justify-center gap-4 mt-12">
              <CarouselPrevious className="relative left-0 translate-x-0 translate-y-0 bg-primary/90 text-foreground-primary border-none hover:bg-primary-hover hover:scale-110 active:scale-95 transition-all duration-[var(--duration-fast)] shadow-medium" />
              <CarouselNext className="relative right-0 translate-x-0 translate-y-0 bg-primary/90 text-foreground-primary border-none hover:bg-primary-hover hover:scale-110 active:scale-95 transition-all duration-[var(--duration-fast)] shadow-medium" />
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}